import { Component, Inject, PLATFORM_ID, OnInit, OnDestroy, DestroyRef } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CoreModule } from './core/core.module';
import { UtmService } from './core/services/utm.service';
import { SvgIconRegistryService } from 'angular-svg-icon';
import { ToasterContainerComponent } from './shared/components/toaster-container/toaster-container.component';
import { StickyZipCtaComponent } from './shared/components/sticky-zip-cta/sticky-zip-cta.component';
import { StickyZipCtaService } from './core/services/sticky-zip-cta.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RoutePaths } from './core/constants/routes';
import { QuestionnaireService } from './core/services/questionnaire.service';
import {
  FIND_ROOFERS_ROUTE_PATTERN,
  ROOF_SCAN_ROUTE_PATTERN,
  TRAFFIC_GENERIC_ROUTES,
} from './constants/routes';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CoreModule, ToasterContainerComponent, StickyZipCtaComponent, CommonModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  animations: [],
})
export class AppComponent implements OnInit {
  isLandingPage = true;
  isQuestionnaire = false;
  isLogoOnlyPage = false;
  showStickyZipCta = false;

  constructor(
    private iconReg: SvgIconRegistryService,
    private utmService: UtmService,
    private router: Router,
    private questionnaireService: QuestionnaireService,
    private stickyZipCtaService: StickyZipCtaService,
    @Inject(PLATFORM_ID) private platformId: object,
    private destroyRef: DestroyRef
  ) {
    this.utmService.captureUtmParams();

    this.loadCriticalIcons();

    if (isPlatformBrowser(this.platformId) && !this.isLandingPage) {
      setTimeout(() => this.loadNonCriticalIcons(), 100);
    }

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (isPlatformBrowser(this.platformId) && (window as any)?.hj) {
          (window as any).hj('stateChange', event.urlAfterRedirects);
        }
        // Re-capture UTM params on navigation to ensure we have the latest
        this.utmService.captureUtmParams();
        // Extract pathname without query parameters for route matching
        const urlPath = event.url.split('?')[0];
        this.isLandingPage = [
          FIND_ROOFERS_ROUTE_PATTERN.test(urlPath),
          ROOF_SCAN_ROUTE_PATTERN.test(urlPath),
          TRAFFIC_GENERIC_ROUTES.includes(
            urlPath as (typeof TRAFFIC_GENERIC_ROUTES)[number]
          ),
          urlPath === `/${RoutePaths.GOOGLE_LANDING}`,
          urlPath === `/${RoutePaths.COMPANY_ESTIMATOR}`,
          urlPath === '/' || urlPath === '', // Homepage is now lead-generation page
          urlPath === `/${RoutePaths.SIDING}`,
        ].some(Boolean);

        if (!urlPath.startsWith(`/${RoutePaths.QUESTIONNAIRE}`)) {
          this.questionnaireService.clearQuestionary();
          this.isQuestionnaire = false;
        } else this.isQuestionnaire = true;

        this.isLogoOnlyPage = urlPath === `/${RoutePaths.PARTNERS}`;
        this.updateStickyZipCta(urlPath);
      }
    });
  }

  private loadCriticalIcons(): void {
    const criticalIcons = [
      'assets/images/logo.svg',
      'assets/images/menu.svg',
      'assets/images/chevron-right.svg',
      'assets/images/telephone-fill.svg',
    ];

    criticalIcons.forEach(icon => {
      this.iconReg.loadSvg(icon)?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    });
  }

  ngOnInit(): void {
    // Google Maps is now lazy-loaded by map-view component when needed
    // No need to preload it here
    
    // Set initial landing page state based on current route
    if (isPlatformBrowser(this.platformId)) {
      const urlPath = this.router.url.split('?')[0];
      this.isLandingPage = [
        FIND_ROOFERS_ROUTE_PATTERN.test(urlPath),
        ROOF_SCAN_ROUTE_PATTERN.test(urlPath),
        TRAFFIC_GENERIC_ROUTES.includes(
          urlPath as (typeof TRAFFIC_GENERIC_ROUTES)[number]
        ),
        urlPath === `/${RoutePaths.GOOGLE_LANDING}`,
        urlPath === `/${RoutePaths.COMPANY_ESTIMATOR}`,
        urlPath === '/' || urlPath === '', // Homepage is now lead-generation page
        urlPath === `/${RoutePaths.SIDING}`,
      ].some(Boolean);
      
      this.isLogoOnlyPage = this.router.url.split('?')[0] === `/${RoutePaths.PARTNERS}`;
      this.updateStickyZipCta(this.router.url.split('?')[0]);

      // Small delay to ensure window.location is fully available after SSR
      setTimeout(() => {
        this.utmService.captureUtmParams();
      }, 0);
    }
  }

  private updateStickyZipCta(urlPath: string): void {
    this.showStickyZipCta = this.stickyZipCtaService.isRouteEnabled(urlPath);
    this.stickyZipCtaService.onRouteChange(urlPath);
  }

  private loadNonCriticalIcons(): void {
    const nonCriticalIcons = [
      'assets/images/roof.svg',
      'assets/images/question-light.svg',
      'assets/images/chevron-left.svg',
      'assets/images/silhouette.svg',
      'assets/images/check.svg',
      'assets/images/caret-right-fill.svg',
      'assets/images/document.svg',
      'assets/images/find-current-promotions.svg',
      'assets/images/connect-with-local-contractors.svg',
      'assets/images/costs.svg',
      'assets/images/contact-logo.svg',
      'assets/images/chat.svg',
      'assets/images/quote.svg',
      'assets/images/pin-map-fill.svg',
      'assets/images/geo-alt-fill.svg',
      'assets/images/house-keys.svg',
      'assets/images/materials.svg',
      'assets/images/profile.svg',
      'assets/images/envelope.svg',
      'assets/images/exclamation-triangle.svg',
      'assets/images/star-fill.svg',
      'assets/images/promotion.svg',
      'assets/images/thank-you.svg',
      'assets/images/xmark.svg',
      'assets/images/info.svg',
      'assets/images/map-pin.svg',
      'assets/images/person.svg',
      'assets/images/arrow-drop-down.svg',
      'assets/images/telephone.svg',
      'assets/images/security.svg',
    ];

    nonCriticalIcons.forEach(icon => {
      this.iconReg.loadSvg(icon)?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    });
  }
}
